import { TaskStatus } from '../hooks/useTaskManager';
import { readValueByPath } from './jsonPath';
import { toStringSafe } from './typeGuards';

/**
 * 任务失败检测可选参数。
 */
export interface FailureDetectionOptions {
  /** 优先扫描的状态路径，默认为通用候选集 */
  statusPaths?: string[];
  /** 判定失败的状态枚举，传入后会与默认集合合并 */
  failureStatuses?: string[];
  /** 错误消息的取值路径，传入后会与默认集合拼接 */
  failureMessagePaths?: string[];
}

/**
 * 任务失败检测结果。
 */
export interface FailureDetectionResult {
  /** 是否判定为失败 */
  failed: boolean;
  /** 原始状态字符串 */
  status?: string;
  /** 检测到的失败消息 */
  message?: string;
}

const DEFAULT_STATUS_PATHS = ['status', 'state', 'task_status', 'taskStatus', 'detail.status', 'detail.state'];
const DEFAULT_FAILURE_STATUSES = ['failed', 'failure', 'error', 'cancelled', 'canceled', 'timeout', 'expired', 'rejected'];
const DEFAULT_FAILURE_MESSAGE_PATHS = [
  'detail.failure_reason',
  'detail.pending_info.failure_reason',
  'failure_reason',
  'detail.error',
  'detail.error_message',
  'error.message',
  'error',
  'message',
];

const normalizeList = (values?: string[]): string[] => {
  if (!Array.isArray(values)) return [];
  return values
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter((value) => value.length > 0);
};

const pickStringByPaths = (source: unknown, paths: string[]): string | undefined => {
  for (const path of paths) {
    if (!path) continue;
    const raw = readValueByPath(source, path, { silent: true });
    const text = toStringSafe(raw)?.trim();
    if (text) {
      return text;
    }
  }
  return undefined;
};

/**
 * 统一的任务状态归一化。
 * 保持与现有 normalizeStatus / normalizeSegmentStatus 的语义一致。
 */
export const normalizeTaskStatus = (status?: string | null): TaskStatus => {
  if (!status) return TaskStatus.PENDING;
  const normalized = status.trim().toLowerCase();
  switch (normalized) {
    case 'submitting':
      return TaskStatus.SUBMITTING;
    case 'queued':
    case 'submitted':
    case 'created':
    case 'pending':
    case 'waiting':
      return TaskStatus.PENDING;
    case 'processing':
    case 'running':
    case 'in_progress':
    case 'in-progress':
    case 'generating':
      return TaskStatus.RUNNING;
    case 'success':
    case 'succeeded':
    case 'completed':
    case 'complete':
    case 'finished':
      return TaskStatus.SUCCESS;
    case 'failed':
    case 'error':
    case 'cancelled':
    case 'canceled':
    case 'failure':
    case 'timeout':
    case 'rejected':
      return TaskStatus.FAILED;
    default:
      return TaskStatus.PENDING;
  }
};

/**
 * 统一进度解析，支持字符串（含百分号）与数字。
 */
export const parseTaskProgress = (value?: unknown): number | undefined => {
  if (value === null || value === undefined) return undefined;

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return undefined;
    const clamped = Math.max(0, Math.min(100, value));
    return Math.floor(clamped);
  }

  const text = toStringSafe(value)?.trim();
  if (!text) return undefined;
  const normalized = text.endsWith('%') ? text.slice(0, -1) : text;
  const parsed = Number.parseFloat(normalized);
  if (!Number.isFinite(parsed)) {
    return undefined;
  }
  if (parsed < 0) return 0;
  if (parsed > 100) return 100;
  return Math.round(parsed);
};

/**
 * 时间戳归一化。
 * - 支持秒或毫秒级数字/字符串
 * - 非法值返回 undefined
 */
export const normalizeTimestamp = (value?: unknown): number | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value <= 0) {
      return undefined;
    }
    if (value > 1_000_000_000_000) {
      return Math.floor(value);
    }
    if (value > 1_000_000_000) {
      return Math.floor(value * 1000);
    }
    return Math.floor(value);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return undefined;
    }
    const parsed = Number.parseFloat(trimmed);
    if (!Number.isFinite(parsed)) {
      return undefined;
    }
    return normalizeTimestamp(parsed);
  }
  return undefined;
};

/**
 * 选择最早的时间戳（忽略非法值）。
 */
export const pickEarliestTimestamp = (candidates: Array<number | undefined>): number | undefined => {
  const values = candidates.filter(
    (ts): ts is number => typeof ts === 'number' && Number.isFinite(ts) && ts > 0
  );
  if (values.length === 0) {
    return undefined;
  }
  return Math.min(...values);
};

/**
 * 选择最晚的时间戳（忽略非法值）。
 */
export const pickLatestTimestamp = (candidates: Array<number | undefined>): number | undefined => {
  const values = candidates.filter(
    (ts): ts is number => typeof ts === 'number' && Number.isFinite(ts) && ts > 0
  );
  if (values.length === 0) {
    return undefined;
  }
  return Math.max(...values);
};

/**
 * 失败状态检测（可配置扫描路径与状态集合）。
 * 保持与 useTaskPolling.detectFailureState 兼容。
 */
export const detectTaskFailure = (
  rawData: unknown,
  options: FailureDetectionOptions = {}
): FailureDetectionResult => {
  const statusPaths = normalizeList(options.statusPaths);
  const effectiveStatusPaths = statusPaths.length > 0 ? statusPaths : DEFAULT_STATUS_PATHS;
  const statusValue = pickStringByPaths(rawData, effectiveStatusPaths);

  const failureCandidates = [
    ...DEFAULT_FAILURE_STATUSES,
    ...normalizeList(options.failureStatuses),
  ];
  const failureSet = new Set(
    failureCandidates.map((item) => item.toLowerCase()).filter((item) => item.length > 0)
  );

  const normalizedStatus = statusValue ? statusValue.toLowerCase() : undefined;
  if (normalizedStatus && failureSet.has(normalizedStatus)) {
    const messagePaths = normalizeList(options.failureMessagePaths);
    const effectiveMessagePaths =
      messagePaths.length > 0 ? [...messagePaths, ...DEFAULT_FAILURE_MESSAGE_PATHS] : DEFAULT_FAILURE_MESSAGE_PATHS;
    const message = pickStringByPaths(rawData, effectiveMessagePaths);
    return {
      failed: true,
      status: statusValue,
      message: message ?? (statusValue ? `任务状态：${statusValue}` : undefined),
    };
  }

  return { failed: false, status: statusValue };
};
